import {
  IconAlertTriangleFilled,
  IconCircle,
  IconCircleCheck,
  IconCircleCheckFilled,
  IconCircleFilled,
  IconCircleX,
  IconCircleXFilled,
} from '@tabler/icons-react';

import { IconFactory } from '@assets';

export enum HelpSectionEnum {
  BATCH_STATUS = 'Batch status',
  DECLARATION_STATUS = 'Declaration status',
  GOOD_ITEMS_STATUS = 'Good items status',
  ACTIONS = 'Batch/ Declaration actions',
  TRANSIT_STATUS = 'Transit status',
  TRANSIT_ACTIONS = 'Transit actions',
  VALUATION_INDICATORS = 'Valuation indicators description',
  TSD_STATUS = 'TSD status',
  NOTIFICATION_INDICATORS = 'Notification indicators',
}

export interface HelpCategoryItem {
  icon?: string | React.ReactNode;
  text: string;
  animationType?: string;
}

export interface HelpSubsection {
  data: HelpCategoryItem[];
  title: string;
}

export interface HelpDataConfig {
  data?: HelpCategoryItem[];
  subsections?: HelpSubsection[];
  fullWidth?: boolean;
  flexRow?: boolean;
}

const ANOMALY_TEXT = {
  HS_CODE_OR_DESCRIPTION: 'Anomalie(s) detected: HS codes and/or description',
  VALUE: 'Anomalie(s) detected: value',
  NO_ANOMALIES: 'No anomalies detected',
};

export const helpData: Record<string, HelpDataConfig> = {
  [HelpSectionEnum.BATCH_STATUS]: {
    data: [
      { icon: IconFactory.upload, text: 'Uploading file, saving declaration and risk analysis. Updating batch' },
      { icon: IconFactory.anomaliesHandled, text: 'All anomalies handled with proposal' },
      { icon: IconFactory.sending, text: 'Sending' },
      { icon: IconFactory.failed, text: 'Invalid file' },
      { icon: IconFactory.customsProcessing, text: 'Validation in progress (waiting for customer answer)' },
      { icon: IconFactory.released, text: 'Declarations released' },
      { icon: IconFactory.anomaliesHSCodeOrDescription, text: ANOMALY_TEXT.HS_CODE_OR_DESCRIPTION },
      { icon: IconFactory.noSentAnomalies, text: ANOMALY_TEXT.NO_ANOMALIES },
      { icon: IconFactory.rejected, text: 'Declarations rejected' },
      { icon: IconFactory.anomalyValue, text: ANOMALY_TEXT.VALUE },
      { icon: IconFactory.notSent, text: 'Not sent' },
      { icon: IconFactory.notReleased, text: 'Declarations not-released' },
      { icon: IconFactory.invalidated, text: 'Declarations invalidated' },
      { icon: IconFactory.processing, text: 'Validation in progress (waiting for customs answer)' },
      { animationType: 'syncing', text: 'Customs notifications synchronization' },
    ],
    flexRow: true,
  },
  [HelpSectionEnum.DECLARATION_STATUS]: {
    data: [
      { icon: IconFactory.notSent, text: 'No anomalies - No risk analysis done' },
      { icon: IconFactory.anomaliesHSCodeOrDescription, text: ANOMALY_TEXT.HS_CODE_OR_DESCRIPTION },
      { icon: IconFactory.anomalyValue, text: ANOMALY_TEXT.VALUE },
      { icon: IconFactory.anomaliesHandled, text: 'Anomalies detected, all handled by user' },
      { icon: IconFactory.noSentAnomalies, text: ANOMALY_TEXT.NO_ANOMALIES },
    ],
  },
  [HelpSectionEnum.GOOD_ITEMS_STATUS]: {
    data: [
      { icon: IconFactory.notSent, text: 'No anomalies - No risk analysis' },
      { icon: IconFactory.anomaliesHSCodeOrDescription, text: ANOMALY_TEXT.HS_CODE_OR_DESCRIPTION },
      { icon: IconFactory.anomalyValue, text: ANOMALY_TEXT.VALUE },
      { icon: IconFactory.anomaliesHandled, text: 'Anomalies detected, no action required' },
      { icon: IconFactory.noSentAnomalies, text: ANOMALY_TEXT.NO_ANOMALIES },
    ],
  },
  [HelpSectionEnum.ACTIONS]: {
    data: [
      { icon: IconFactory.send, text: 'Sent' },
      { icon: IconFactory.actions, text: 'Batch/ Declaration actions' },
      { icon: IconFactory.delete, text: 'Delete' },
      { icon: IconFactory.archive, text: 'Archive/Unarchive' },
      { icon: IconFactory.view, text: 'View details' },
      { icon: IconFactory.mail, text: 'Send by email' },
      { icon: IconFactory.update, text: 'Update' },
      { icon: IconFactory.download, text: 'Download file' },
      { icon: IconFactory.report, text: 'Generate customs report' },
      { icon: IconFactory.refactor, text: 'Edit' },
      { icon: IconFactory.viewImage, text: 'View image' },
      { icon: IconFactory.customsSync, text: 'Customs sync' },
      { icon: IconFactory.riskAnalysisReport, text: 'Risk analysis report' },
      { icon: IconFactory.sendRiskAnalysisToCustomer, text: 'Send risk analysis to customer' },
    ],
    fullWidth: false,
    flexRow: true,
  },

  [HelpSectionEnum.TRANSIT_STATUS]: {
    subsections: [
      {
        title: 'Departure',
        data: [
          { icon: IconFactory.notSent, text: 'Not sent' },
          { icon: IconFactory.sending, text: 'Sending' },
          { icon: IconFactory.processing, text: 'Sent' },
          { icon: IconFactory.processing, text: 'Accepted' },
          { icon: IconFactory.rejected, text: 'Rejected' },
          { icon: IconFactory.anomalyValue, text: 'In control' },
          { icon: IconFactory.processing, text: 'Corr refused waiting' },
          { icon: IconFactory.notReleased, text: 'No release for transit' },
          { icon: IconFactory.processing, text: 'Corr ok waiting' },
          { icon: IconFactory.processing, text: 'Invalidation pending' },
          { icon: IconFactory.invalidated, text: 'Transits invalidated' },
          { icon: IconFactory.rejected, text: 'Guarantee not valid' },
          { icon: IconFactory.lightGreenSuccess, text: 'Unload permission' },
          { icon: IconFactory.lightGreenSuccess, text: 'Released' },
          { icon: IconFactory.processing, text: 'Remarks sent' },
          { icon: IconFactory.released, text: 'Finished' },
          { icon: IconFactory.greenRejected, text: 'Discrepancies' },
          { icon: IconFactory.anomalyValue, text: 'Requested non arrived' },
        ],
      },
      {
        title: 'Arrival',
        data: [
          { icon: IconFactory.notSent, text: 'Not sent' },
          { icon: IconFactory.processing, text: 'Sent' },
          { icon: IconFactory.processing, text: 'Accepted' },
          { icon: IconFactory.processing, text: 'Remarks sent' },
          { icon: IconFactory.sending, text: 'Sending' },
          { icon: IconFactory.rejected, text: 'Rejected' },
          { icon: IconFactory.lightGreenSuccess, text: 'Unload permission received' },
          { icon: IconFactory.released, text: 'Released' },
        ],
      },
    ],
  },
  [HelpSectionEnum.TRANSIT_ACTIONS]: {
    data: [
      { icon: IconFactory.view, text: 'View details' },
      { icon: IconFactory.delete, text: 'Delete transit' },
      { icon: IconFactory.send, text: 'Send' },
      { icon: IconFactory.finalize, text: 'Finalize' },
      { icon: IconFactory.report, text: 'Genrate or download a customs report or follow letter' },
      { icon: IconFactory.copy, text: 'Duplicate transit' },
      { icon: IconFactory.customsSync, text: 'Customs sync' },
    ],
    fullWidth: false,
    flexRow: true,
  },
  [HelpSectionEnum.VALUATION_INDICATORS]: {
    data: [
      { text: 'The code comprises four digits, each of which being either a "0" or a "1".' },
      { text: 'Each "1" or "0" digit reflects whether or not a valuation indicator is relevant to the valuation of the goods concerned.' },
      { text: '1st digit: Party relationship, whether there is price influence or not' },
      { text: '2nd digit: Restrictions as to the disposal or use of the goods by the buyer in accordance with Article 70(3)(a) of the Code.' },
      { text: '3rd digit: Sale or price is subject to some condition or consideration in accordance with Article 70(3)(b) of the Code.' },
      {
        text: `4th digit: The sale is subject to an arrangement under which part of the proceeds of any subsequent resale,
         disposal or use accrues directly or indirectly to the seller`,
      },
      {
        text: `Example: Goods subject to party relationship, but not to any of the other situations defined under 2nd,
         3rd and 4th digits would entail the use of code combination "1 000."`,
      },
      { text: 'Standard sales between unrelated parties uses combination 0000' },
    ],
    fullWidth: true,
    flexRow: false,
  },
  [HelpSectionEnum.TSD_STATUS]: {
    data: [
      { icon: <IconCircle color="grey" />, text: 'Not sent' },
      { icon: <IconCircleFilled color="blue" />, text: 'Sent' },
      { icon: <IconCircleCheckFilled color="blue" />, text: 'Pre-lodged' },
      { icon: <IconCircle color="blue" />, text: 'Presented' },
      { icon: <IconCircleCheck color="blue" />, text: 'Linked' },
      { icon: <IconCircleX color="red" />, text: 'Not linked' },
      { icon: <IconCircleCheckFilled color="#69db7c" />, text: 'Accepted' },
      { icon: <IconCircleXFilled color="grey" />, text: 'Invalidated' },
      { icon: <IconCircleXFilled color="orange" />, text: 'Under control' },
      { icon: <IconAlertTriangleFilled color="orange" />, text: 'Irregularity under investigation' },
      { icon: <IconAlertTriangleFilled color="red" />, text: 'Measures required' },
      { icon: <IconCircleCheckFilled color="green" />, text: 'Temporary storage ended' },
      { icon: <IconCircleXFilled color="red" />, text: 'Rejected' },
      { icon: <IconCircleFilled color="green" />, text: 'Cleared' },
    ],
    flexRow: true,
  },
  [HelpSectionEnum.NOTIFICATION_INDICATORS]: {
    data: [
      { icon: <IconCircleFilled size={20} color="red" />, text: 'Actions pending' },
      { icon: <IconCircleFilled size={20} color="yellow" />, text: 'Notifications pending' },
      { icon: IconFactory.comment, text: 'Comments' },
    ],
    fullWidth: false,
    flexRow: true,
  },
};
